<script setup lang="ts">
import PageContainer from "~/components/ui/PageContainer.vue";
import ExerciseMCQ from "~/features/computer/components/ExerciseMCQ.vue";
import { buildComputerNamedItemList } from "~/features/computer/data/seo/computer-schema";
import { computerMcqChapterBySlug, computerMcqChapters } from "~/features/computer/data/mcq-registry";

const route = useRoute();
const slug = String(route.params.slug);
const chapter = computerMcqChapterBySlug[slug];

if (!chapter) {
  throw createError({
    statusCode: 404,
    statusMessage: "MCQ chapter page not found",
  });
}

const chapterQuestions = chapter.questions;
const previewQuestions = chapterQuestions.slice(0, 6);
const relatedClusters = computerMcqChapters.filter((item) => item.slug !== chapter.slug).slice(0, 4);
const faqs = [
  {
    question: `${chapter.title} ke MCQs kahan milte hain?`,
    answer: `${chapter.title} ke MCQs isi cluster page aur full chapter MCQs page par milte hain.`,
  },
  {
    question: `${chapter.title} ke important MCQs kaise prepare karein?`,
    answer: `Pehle preview MCQs revise karein, phir full chapter MCQs aur chapter hub se related pages kholo.`,
  },
  {
    question: `Kya ${chapter.title} se board objective questions aate hain?`,
    answer: `Haan, ${chapter.shortTitle} chapter se objective questions aate hain. Is liye chapter-wise MCQ practice useful rehti hai.`,
  },
];

usePageSeo({
  title: chapter.seo.title,
  description: chapter.seo.description,
  keywords: chapter.seo.keywords,
  faqs,
  breadcrumbs: [
    { name: "Home", item: "/" },
    { name: "11th Computer", item: "/11th/computer/" },
    { name: "MCQs", item: "/11th/computer/mcqs/" },
    { name: chapter.title, item: chapter.routes.clusterTo },
  ],
  schemaType: "CollectionPage",
  schemaProperties: {
    about: {
      "@type": "Thing",
      name: `${chapter.title} MCQ Cluster`,
    },
    mainEntity: buildComputerNamedItemList(chapterQuestions.map((item) => item.question)),
  },
});
</script>

<template>
  <PageContainer>
    <section class="rounded-[30px] border border-slate-200 bg-gradient-to-br from-sky-50 via-white to-amber-50 px-5 py-6 shadow-sm dark:border-slate-800 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950 md:px-8 md:py-8">
      <p class="text-xs font-semibold uppercase tracking-[0.24em] text-sky-700">Chapter-wise Cluster</p>
      <h1 class="mt-3 max-w-3xl text-3xl font-bold tracking-tight text-slate-950 dark:text-white md:text-5xl">
        11th Class Computer MCQs {{ chapter.title }}
      </h1>
      <p class="mt-4 max-w-3xl text-sm leading-7 text-slate-700 dark:text-slate-300">
        {{ chapter.description }} Is page par aap ko quick objective shortlist, real MCQ preview aur full chapter practice links milte hain.
      </p>

      <div class="mt-6 flex flex-wrap gap-3">
        <a
          href="#mcq-list"
          class="inline-flex items-center justify-center rounded-full bg-slate-950 px-5 py-3 text-sm font-semibold text-white transition hover:bg-sky-700"
        >
          View MCQs
        </a>
        <NuxtLink
          :to="chapter.routes.mcqTo"
          class="inline-flex items-center justify-center rounded-full border border-slate-300 bg-white px-5 py-3 text-sm font-semibold text-slate-900 transition hover:border-sky-300 hover:text-sky-700 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 dark:hover:border-sky-600 dark:hover:text-sky-300"
        >
          Full Chapter MCQs
        </NuxtLink>
      </div>
    </section>

    <section class="grid gap-4 md:grid-cols-3">
      <article class="rounded-[24px] border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900">
        <p class="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 dark:text-slate-400">Chapter</p>
        <h2 class="mt-3 text-4xl font-bold tracking-tight text-slate-950 dark:text-white">{{ chapter.chapter }}</h2>
        <p class="mt-2 text-sm leading-7 text-slate-600 dark:text-slate-300">{{ chapter.shortTitle }}</p>
      </article>
      <article class="rounded-[24px] border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900">
        <p class="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 dark:text-slate-400">Preview MCQs</p>
        <h2 class="mt-3 text-4xl font-bold tracking-tight text-slate-950 dark:text-white">{{ previewQuestions.length }}</h2>
        <p class="mt-2 text-sm leading-7 text-slate-600 dark:text-slate-300">Quick preview questions</p>
      </article>
      <article class="rounded-[24px] border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900">
        <p class="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 dark:text-slate-400">Available</p>
        <h2 class="mt-3 text-4xl font-bold tracking-tight text-slate-950 dark:text-white">{{ chapterQuestions.length }}</h2>
        <p class="mt-2 text-sm leading-7 text-slate-600 dark:text-slate-300">Questions in this chapter flow</p>
      </article>
    </section>

    <section class="rounded-[28px] border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900 md:p-6">
      <div>
        <p class="text-xs font-semibold uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">Quick Preview</p>
        <h2 class="mt-2 text-2xl font-semibold tracking-tight text-slate-950 dark:text-white">Important MCQs from {{ chapter.shortTitle }}</h2>
      </div>
      <div class="mt-5 space-y-4">
        <ExerciseMCQ
          v-for="question in previewQuestions"
          :key="`${question.unit}-${question.id}`"
          :question="question"
        />
      </div>
    </section>

    <section id="mcq-list" class="rounded-[28px] border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900 md:p-6">
      <div class="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p class="text-xs font-semibold uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">Question Bank</p>
          <h2 class="mt-2 text-2xl font-semibold tracking-tight text-slate-950 dark:text-white">All listed MCQs for {{ chapter.shortTitle }}</h2>
        </div>
        <p class="text-sm font-medium text-slate-500 dark:text-slate-400">Scannable list</p>
      </div>
      <ol class="mt-5 space-y-2 text-sm leading-7 text-slate-700 dark:text-slate-300">
        <li v-for="question in chapterQuestions" :key="`list-${question.unit}-${question.id}`">
          {{ question.question }}
        </li>
      </ol>
    </section>

    <section class="grid gap-4 xl:grid-cols-[1.15fr_0.85fr]">
      <div class="rounded-[28px] border border-slate-200 bg-slate-950 p-5 text-white md:p-6">
        <p class="text-xs font-semibold uppercase tracking-[0.22em] text-sky-300">Next Step</p>
        <h2 class="mt-2 text-2xl font-semibold tracking-tight">Open the full chapter flow</h2>
        <div class="mt-5 grid gap-3">
          <NuxtLink :to="chapter.routes.notesTo" class="rounded-[18px] border border-white/10 bg-white/5 px-4 py-3 text-sm font-medium transition hover:bg-white/10">
            Notes
          </NuxtLink>
          <NuxtLink :to="chapter.routes.mcqTo" class="rounded-[18px] border border-white/10 bg-white/5 px-4 py-3 text-sm font-medium transition hover:bg-white/10">
            Full MCQs
          </NuxtLink>
          <NuxtLink :to="chapter.routes.exerciseTo" class="rounded-[18px] border border-white/10 bg-white/5 px-4 py-3 text-sm font-medium transition hover:bg-white/10">
            Exercise
          </NuxtLink>
          <NuxtLink :to="chapter.routes.chapterTo" class="rounded-[18px] border border-white/10 bg-white/5 px-4 py-3 text-sm font-medium transition hover:bg-white/10">
            Chapter Hub
          </NuxtLink>
        </div>
      </div>

      <div class="rounded-[28px] border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900 md:p-6">
        <p class="text-xs font-semibold uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">More Chapters</p>
        <h2 class="mt-2 text-2xl font-semibold tracking-tight text-slate-950 dark:text-white">Continue chapter-wise</h2>
        <div class="mt-5 space-y-3">
          <NuxtLink
            v-for="item in relatedClusters"
            :key="item.slug"
            :to="item.routes.clusterTo"
            class="block rounded-[18px] border border-slate-200 bg-slate-50 px-4 py-3 text-sm font-medium text-slate-900 transition hover:border-sky-300 hover:bg-white dark:border-slate-800 dark:bg-slate-950 dark:text-slate-100 dark:hover:border-sky-700 dark:hover:bg-slate-900"
          >
            Chapter {{ item.chapter }} - {{ item.shortTitle }}
          </NuxtLink>
        </div>
      </div>
    </section>

    <section class="rounded-[28px] border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900 md:p-6">
      <div>
        <p class="text-xs font-semibold uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">FAQ</p>
        <h2 class="mt-2 text-2xl font-semibold tracking-tight text-slate-950 dark:text-white">{{ chapter.shortTitle }} MCQs FAQs</h2>
      </div>
      <div class="mt-5 space-y-4">
        <article
          v-for="faq in faqs"
          :key="faq.question"
          class="rounded-[22px] border border-slate-200 bg-slate-50 p-5 dark:border-slate-800 dark:bg-slate-950"
        >
          <h3 class="text-lg font-semibold text-slate-950 dark:text-white">{{ faq.question }}</h3>
          <p class="mt-2 text-sm leading-7 text-slate-600 dark:text-slate-300">{{ faq.answer }}</p>
        </article>
      </div>
    </section>
  </PageContainer>
</template>
